import { Customer, CustomerRepository } from '../domain/CustomerDomain';
import { query_dispatcher_get, sync_command_dispatcher, async_command_dispatcher, client } from '@/api/client';
import { Party } from '@/modules/facturae/domain/Invoiceable';
import { CommandDispatcher } from '@/modules/shared/domain/CommandDispatcher';

export class ApiCustomerRepository implements CustomerRepository {
  command_dispatcher: CommandDispatcher;

  constructor() {
    this.command_dispatcher = sync_command_dispatcher;
  }

  async findAll(companyId: string): Promise<Customer[]> {
    const allData = await this.fetchAllPaginated(companyId);
    return allData.map(dto => Customer.fromDTO(dto, companyId));
  }

  private async fetchAllPaginated(
    companyId: string,
    limit: number = 100,
    maxItems: number = 10000
  ): Promise<any[]> {
    const fetchBatch = async (offset: number = 0, accumulated: any[] = []): Promise<any[]> => {
      if (offset >= maxItems) {
        console.warn(`Reached safety limit of ${maxItems} items`);
        return accumulated;
      }

      const response = await query_dispatcher_get.dispatch('get.facture.e.customers', {
        company_id: companyId,
        search_term: '',
        limit,
        offset
      });

      const items = Array.isArray(response) ? response : [];

      if (items.length === 0) {
        return accumulated;
      }

      return fetchBatch(offset + limit, [...accumulated, ...items]);
    };

    return fetchBatch();
  }

  async save(customer: Customer): Promise<Customer> {
    // Convert Customer to Party for the new save_third endpoint
    const party = new Party({
      id: customer.id,
      company_id: customer.companyId,
      cif: customer.cif || '',
      post_code: customer.postCode || '',
      type: customer.type || 'juridica',
      name: customer.name,
      first_surname: customer.firstSurname,
      second_surname: customer.secondSurname,
      business_name: customer.businessName,
      trade_name: customer.tradeName,
      address: customer.address,
      city: customer.city,
      province: customer.province,
      country: customer.country,
      email: customer.email,
      phone: customer.phone,
      website: customer.website,
      DIRe: customer.DIRe,
      accounting_office: customer.accountingOffice,
      management_body: customer.managementBody,
      processing_unit: customer.processingUnit,
      proposing_body: customer.proposingBody,
      bank_account_number: customer.bankAccountNumber,
      bic: customer.bic,
      mandate_id: customer.mandateId,
      mandate_signature_date: customer.mandateSignatureDate,
      debit_account: customer.debitAccount,
      payment_means: customer.paymentMeans,
      representation_data: undefined,
      payment_information: customer.paymentInformation,
      pdf_language: customer.pdfLanguage,
      timestamp_modified: customer.timestampModified
    });

    const res = await this.command_dispatcher.dispatch('save.third', { third: party });
    const result = res.output;

    if (result.status === 'error') {
      throw new Error(typeof result.message === 'string' ? result.message : 'Error saving customer');
    }

    return customer;
  }

  async importFromExcel(
    companyId: string,
    fileContent: ArrayBuffer,
    filename: string
  ): Promise<{ asyncJobId?: number }> {
    // Convert ArrayBuffer to base64
    const bytes = new Uint8Array(fileContent);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    const base64Content = window.btoa(binary);

    const response = await async_command_dispatcher.dispatch('import.customers.from.excel', {
      cdb_id: companyId,
      file_content: base64Content,
      filename: filename
    });

    return {
      asyncJobId: response.job_id
    };
  }

  async downloadTemplate(): Promise<string> {
    const response = await client.post('query/', {
      query: 'download.customers.template',
      args: {}
    });

    return response.data; // Returns URL string
  }
}